import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  Switch,
  TextField,
  Tooltip,
} from '@material-ui/core';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import CloseIcon from '@material-ui/icons/Close';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import PowerSettingsNewIcon from '@material-ui/icons/PowerSettingsNew';
import RefreshIcon from '@material-ui/icons/Refresh';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import SearchFeaturePageShell from 'components/shell/SearchFeaturePageShell';
import { useLocalizedMessage } from 'util/useLocalizedMessage';
import { useLocalizedNotifications } from 'util/useLocalizedNotifications';
import { useShellAppContext } from 'features/shell/context/useShellAppContext';
import { useAgentMcpStatus } from './useAgentMcpStatus';
import {
  generateAgentMcpPassword,
  generateAgentMcpUsername,
  validateAgentMcpPassword,
  validateAgentMcpUsername,
} from './passwordPolicy';
import { copySecretText, copyText } from './copyText';
import {
  createBasicAuthorizationHeader,
  normalizeAgentMcpUsername,
} from './credentialHandoff';
import { assertAgentMcpConfigurePostcondition } from './bridgeAgentMcpApi';
import styles from './AgentMcpSettingsPage.module.scss';

const DOCS_URL = 'https://docs.kealabs.com/';
const ERROR_MESSAGE_KEYS = {
  UNKNOWN_ACTION: 'agentMcp.error.requestRejected',
  INVALID_PAYLOAD: 'agentMcp.error.requestRejected',
  INSECURE_TRANSPORT: 'agentMcp.error.insecureTransport',
  INVALID_CREDENTIALS: 'agentMcp.error.invalidCredentials',
  PERSISTENCE_FAILED: 'agentMcp.error.persistence',
  CREDENTIALS_REQUIRED: 'agentMcp.error.credentialsRequired',
  INVALID_AGENT_MCP_STATE: 'agentMcp.error.invalidResponse',
  AGENT_MCP_CONFIGURE_POSTCONDITION_FAILED: 'agentMcp.error.invalidResponse',
  SECURE_RANDOM_UNAVAILABLE: 'agentMcp.error.secureRandom',
};

function errorMessageKey(error, fallback) {
  return ERROR_MESSAGE_KEYS[error?.code] || fallback;
}

function createUsername() {
  try {
    return generateAgentMcpUsername();
  } catch (_error) {
    return 'kea-agent-user';
  }
}

function statusCopy(status, t) {
  return {
    ready: {
      label: t('agentMcp.status.ready.label'),
      description: null,
      tone: 'ready',
    },
    disabled: {
      label: t('agentMcp.status.disabled.label'),
      description: null,
      tone: 'disabled',
    },
    transport_blocked: {
      label: t('agentMcp.status.blocked.label'),
      description: t('agentMcp.transport.blocked.description'),
      tone: 'blocked',
    },
    needs_credentials: {
      label: t('agentMcp.status.setup.label'),
      description: t('agentMcp.status.setup.description'),
      tone: 'setup',
    },
  }[status] || {
    label: t('agentMcp.status.unavailable.label'),
    description: t('agentMcp.status.unavailable.description'),
    tone: 'disabled',
  };
}

export default function AgentMcpSettingsPage({ api: injectedApi = null }) {
  const t = useLocalizedMessage();
  const notify = useLocalizedNotifications();
  const { bridgeClient, capabilities } = useShellAppContext();
  const supported = capabilities?.agentMcpAdminV1 === true;
  const {
    api,
    state,
    setState,
    isLoading,
    error: loadError,
    setError: setLoadError,
    reload,
  } = useAgentMcpStatus({ enabled: supported, bridgeClient, api: injectedApi });
  const [username, setUsername] = useState(createUsername);
  const [password, setPassword] = useState('');
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [credentialHandoff, setCredentialHandoff] = useState(null);
  const [revealedHandoffFields, setRevealedHandoffFields] = useState({
    password: false,
    authorizationHeader: false,
  });
  const handoffPanelRef = useRef(null);
  const savedUsername = state?.configured ? (state.username || '') : '';

  useEffect(() => {
    if (savedUsername) setUsername(savedUsername);
  }, [savedUsername]);

  useEffect(() => {
    if (credentialHandoff && handoffPanelRef.current) {
      handoffPanelRef.current.focus();
    }
  }, [credentialHandoff]);

  const usernameValid = validateAgentMcpUsername(username);
  const passwordValid = validateAgentMcpPassword(password);
  const canSubmit = usernameValid && passwordValid && !isSaving;
  const copy = statusCopy(state?.status, t);
  const StatusIcon = state?.ready
    ? CheckCircleOutlineIcon
    : state?.status === 'transport_blocked'
      ? ErrorOutlineIcon
      : state?.status === 'needs_credentials'
        ? VpnKeyIcon
        : PowerSettingsNewIcon;

  const perform = async (operation, successMessageKey) => {
    setIsSaving(true);
    try {
      const next = await operation();
      setState(next);
      notify.success(successMessageKey, {}, 3500);
      return next;
    } catch (nextError) {
      notify.error(errorMessageKey(nextError, 'agentMcp.error.action'), {}, 5000);
      return null;
    } finally {
      setIsSaving(false);
    }
  };

  const handleDismissHandoff = () => {
    setCredentialHandoff(null);
    setRevealedHandoffFields({ password: false, authorizationHeader: false });
    setCopiedField(null);
  };

  const handleConfigure = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;
    const submittedUsername = normalizeAgentMcpUsername(username);
    const submittedPassword = password;
    handleDismissHandoff();
    const savedState = await perform(
      async () => assertAgentMcpConfigurePostcondition(
        await api.configure({ username: submittedUsername, password: submittedPassword }),
        submittedUsername,
      ),
      state?.configured ? 'agentMcp.feedback.updated' : 'agentMcp.feedback.configured',
    );
    if (savedState) {
      setRevealedHandoffFields({ password: false, authorizationHeader: false });
      setCredentialHandoff({
        endpoint: savedState.endpoint,
        username: submittedUsername,
        password: submittedPassword,
        authorizationHeader: createBasicAuthorizationHeader(
          submittedUsername,
          submittedPassword,
        ),
      });
      setPassword('');
      setPasswordVisible(false);
      setCopiedField(null);
    }
  };

  const handleGenerate = () => {
    try {
      setPassword(generateAgentMcpPassword());
      setPasswordVisible(false);
      setCopiedField(null);
    } catch (nextError) {
      notify.error(errorMessageKey(nextError, 'agentMcp.error.action'), {}, 5000);
    }
  };

  const handleGenerateUsername = () => {
    try {
      setUsername(generateAgentMcpUsername());
      setCopiedField(null);
    } catch (nextError) {
      notify.error(errorMessageKey(nextError, 'agentMcp.error.action'), {}, 5000);
    }
  };

  const handleCopy = async (field, value, secret = false) => {
    const copied = await (secret ? copySecretText(value) : copyText(value));
    setCopiedField(copied ? field : `${field}-failed`);
  };

  const handleToggle = event => perform(
    () => api.setEnabled(event.target.checked),
    event.target.checked ? 'agentMcp.feedback.enabled' : 'agentMcp.feedback.disabled',
  );

  const handleRevoke = async () => {
    const revoked = await perform(() => api.revoke(), 'agentMcp.feedback.revoked');
    if (revoked) {
      handleDismissHandoff();
      setPassword('');
      setUsername(createUsername());
      setRevokeOpen(false);
    }
  };

  const shellError = loadError
    ? t(errorMessageKey(loadError, 'agentMcp.error.load'))
    : null;

  return (
    <SearchFeaturePageShell
      pageId="agentMcpSettings"
      description={t('agentMcp.page.description')}
      isLoading={isLoading}
      error={shellError}
      onRetry={() => {
        handleDismissHandoff();
        setLoadError(null);
        reload();
      }}
      secondaryActions={(
        <Button
          component={Link}
          href={DOCS_URL}
          target="_blank"
          rel="noopener noreferrer"
          endIcon={<OpenInNewIcon />}
        >
          {t('agentMcp.docs.open')}
        </Button>
      )}
    >
      {state && (
        <div className={styles.page}>
          <section
            className={`${styles.statusPanel} ${styles[`statusPanel_${copy.tone}`]}`}
            data-testid="agent-mcp-status-card"
          >
            <div className={styles.statusIdentity}>
              <span className={styles.statusIcon} aria-hidden="true">
                <StatusIcon />
              </span>
              <div className={styles.statusCopy}>
                <h2 aria-live="polite">{copy.label}</h2>
                {copy.description && <p>{copy.description}</p>}
              </div>
            </div>
            <div className={styles.toggleControl}>
              {!state.configured && (
                <span className={styles.toggleHint} id="agent-mcp-enable-hint">
                  {t('agentMcp.status.setup.enableHint')}
                </span>
              )}
              <Tooltip
                title={!state.configured ? t('agentMcp.status.setup.enableHint') : ''}
              >
                <span className={styles.switchTarget}>
                  <Switch
                    checked={state.enabled}
                    onChange={handleToggle}
                    color="default"
                    classes={{
                      root: styles.switchRoot,
                      switchBase: styles.switchBase,
                      thumb: styles.switchThumb,
                      track: styles.switchTrack,
                      checked: styles.switchChecked,
                      disabled: styles.switchDisabled,
                      input: styles.switchInput,
                    }}
                    disabled={isSaving || !state.configured || state.status === 'transport_blocked'}
                    inputProps={{
                      'aria-label': t('agentMcp.status.toggle'),
                      'aria-describedby': !state.configured ? 'agent-mcp-enable-hint' : undefined,
                    }}
                  />
                </span>
              </Tooltip>
            </div>
          </section>

          {credentialHandoff && (
            <section
              ref={handoffPanelRef}
              className={styles.handoffPanel}
              aria-labelledby="agent-mcp-handoff-title"
              data-testid="agent-mcp-credential-handoff"
              tabIndex="-1"
            >
              <header className={styles.handoffHeader}>
                <span className={styles.handoffIcon} aria-hidden="true">
                  <CheckCircleOutlineIcon />
                </span>
                <div className={styles.handoffHeading}>
                  <h2 id="agent-mcp-handoff-title">{t('agentMcp.handoff.title')}</h2>
                  <p>{t('agentMcp.handoff.description')}</p>
                </div>
                <Tooltip title={t('agentMcp.handoff.close')}>
                  <IconButton
                    className={styles.handoffClose}
                    type="button"
                    aria-label={t('agentMcp.handoff.close')}
                    onClick={handleDismissHandoff}
                  >
                    <CloseIcon />
                  </IconButton>
                </Tooltip>
              </header>

              <div className={styles.handoffGrid}>
                <div className={styles.handoffField}>
                  <span className={styles.fieldLabel}>{t('agentMcp.endpoint.label')}</span>
                  <div className={styles.handoffValueRow}>
                    <code className={styles.handoffValue}>{credentialHandoff.endpoint}</code>
                    <Tooltip title={t('agentMcp.copy.endpoint')}>
                      <IconButton
                        type="button"
                        aria-label={t('agentMcp.copy.endpoint')}
                        onClick={() => handleCopy('handoff-endpoint', credentialHandoff.endpoint)}
                      >
                        {copiedField === 'handoff-endpoint'
                          ? <CheckCircleOutlineIcon />
                          : <FileCopyOutlinedIcon />}
                      </IconButton>
                    </Tooltip>
                  </div>
                </div>

                <div className={styles.handoffField}>
                  <span className={styles.fieldLabel}>{t('agentMcp.username.label')}</span>
                  <div className={styles.handoffValueRow}>
                    <code className={styles.handoffValue}>{credentialHandoff.username}</code>
                    <Tooltip title={t('agentMcp.copy.username')}>
                      <IconButton
                        type="button"
                        aria-label={t('agentMcp.copy.username')}
                        onClick={() => handleCopy('handoff-username', credentialHandoff.username)}
                      >
                        {copiedField === 'handoff-username'
                          ? <CheckCircleOutlineIcon />
                          : <FileCopyOutlinedIcon />}
                      </IconButton>
                    </Tooltip>
                  </div>
                </div>

                <div className={styles.handoffField}>
                  <span className={styles.fieldLabel}>{t('agentMcp.password.label')}</span>
                  <div className={styles.handoffValueRow}>
                    <code className={styles.handoffValue} data-testid="agent-mcp-handoff-password">
                      {revealedHandoffFields.password
                        ? credentialHandoff.password
                        : '••••••••••••'}
                    </code>
                    <Tooltip title={revealedHandoffFields.password
                      ? t('agentMcp.password.hide')
                      : t('agentMcp.password.show')}>
                      <IconButton
                        type="button"
                        aria-label={revealedHandoffFields.password
                          ? t('agentMcp.password.hide')
                          : t('agentMcp.password.show')}
                        aria-pressed={revealedHandoffFields.password}
                        onClick={() => setRevealedHandoffFields(fields => ({
                          ...fields,
                          password: !fields.password,
                        }))}
                      >
                        {revealedHandoffFields.password
                          ? <VisibilityOffIcon />
                          : <VisibilityIcon />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('agentMcp.copy.password')}>
                      <IconButton
                        type="button"
                        aria-label={t('agentMcp.copy.password')}
                        onClick={() => handleCopy(
                          'handoff-password',
                          credentialHandoff.password,
                          true,
                        )}
                      >
                        {copiedField === 'handoff-password'
                          ? <CheckCircleOutlineIcon />
                          : <FileCopyOutlinedIcon />}
                      </IconButton>
                    </Tooltip>
                  </div>
                </div>

                <div className={styles.handoffField}>
                  <span className={styles.fieldLabel}>{t('agentMcp.handoff.authorizationHeader')}</span>
                  <div className={styles.handoffValueRow}>
                    <code className={styles.handoffValue} data-testid="agent-mcp-handoff-header">
                      {revealedHandoffFields.authorizationHeader
                        ? credentialHandoff.authorizationHeader
                        : 'Basic ••••••••••••'}
                    </code>
                    <Tooltip title={revealedHandoffFields.authorizationHeader
                      ? t('agentMcp.handoff.header.hide')
                      : t('agentMcp.handoff.header.show')}>
                      <IconButton
                        type="button"
                        aria-label={revealedHandoffFields.authorizationHeader
                          ? t('agentMcp.handoff.header.hide')
                          : t('agentMcp.handoff.header.show')}
                        aria-pressed={revealedHandoffFields.authorizationHeader}
                        onClick={() => setRevealedHandoffFields(fields => ({
                          ...fields,
                          authorizationHeader: !fields.authorizationHeader,
                        }))}
                      >
                        {revealedHandoffFields.authorizationHeader
                          ? <VisibilityOffIcon />
                          : <VisibilityIcon />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('agentMcp.copy.authorizationHeader')}>
                      <IconButton
                        type="button"
                        aria-label={t('agentMcp.copy.authorizationHeader')}
                        onClick={() => handleCopy(
                          'handoff-authorization',
                          credentialHandoff.authorizationHeader,
                          true,
                        )}
                      >
                        {copiedField === 'handoff-authorization'
                          ? <CheckCircleOutlineIcon />
                          : <FileCopyOutlinedIcon />}
                      </IconButton>
                    </Tooltip>
                  </div>
                </div>
              </div>

              {copiedField && copiedField.startsWith('handoff-') && (
                <p
                  className={copiedField.endsWith('-failed')
                    ? styles.copyError
                    : styles.copyFeedback}
                  role="status"
                >
                  {copiedField.endsWith('-failed')
                    ? t('agentMcp.copy.secretFailed')
                    : t('agentMcp.copy.copied')}
                </p>
              )}

              <footer className={styles.handoffFooter}>
                <p>{t('agentMcp.handoff.oneTime')}</p>
                <Button
                  className={styles.handoffDismiss}
                  type="button"
                  variant="outlined"
                  onClick={handleDismissHandoff}
                >
                  {t('agentMcp.handoff.dismiss')}
                </Button>
              </footer>
            </section>
          )}

          <div className={styles.workspace}>
            <form
              className={styles.primaryPanel}
              onSubmit={handleConfigure}
              autoComplete="on"
              data-testid="agent-mcp-credentials-form"
            >
              <header className={styles.panelHeader}>
                <span className={styles.panelIcon} aria-hidden="true"><VpnKeyIcon /></span>
                <div>
                  <h2>{t('agentMcp.credentials.title')}</h2>
                  <p>
                    {state.configured
                      ? t('agentMcp.credentials.updateDescription')
                      : t('agentMcp.credentials.description')}
                  </p>
                </div>
              </header>

              <div className={styles.endpointBlock}>
                <span className={styles.fieldLabel} id="agent-mcp-endpoint-label">
                  {t('agentMcp.endpoint.label')}
                </span>
                <div className={styles.endpointRow} aria-labelledby="agent-mcp-endpoint-label">
                  <code
                    className={styles.endpointValue}
                    aria-label={t('agentMcp.endpoint.label')}
                    data-testid="agent-mcp-endpoint"
                  >
                    {state.endpoint}
                  </code>
                  <Tooltip title={t('agentMcp.copy.endpoint')}>
                    <Button
                      className={styles.copyButton}
                      type="button"
                      aria-label={t('agentMcp.copy.endpoint')}
                      onClick={() => handleCopy('endpoint', state.endpoint)}
                      startIcon={copiedField === 'endpoint'
                        ? <CheckCircleOutlineIcon />
                        : <FileCopyOutlinedIcon />}
                    >
                      {copiedField === 'endpoint'
                        ? t('agentMcp.copy.copied')
                        : t('agentMcp.actions.copy')}
                    </Button>
                  </Tooltip>
                </div>
                {copiedField === 'endpoint-failed' && (
                  <span className={styles.fieldHelpError} role="status">
                    {t('agentMcp.copy.failed')}
                  </span>
                )}
              </div>

              <div className={styles.formBody}>
                <div className={styles.formGrid}>
                  <TextField
                    id="agent-mcp-username"
                    className={styles.textField}
                    label={t('agentMcp.username.label')}
                    value={username}
                    onChange={(event) => {
                      setUsername(event.target.value);
                      setCopiedField(null);
                    }}
                    onBlur={() => setUsername(value => (
                      value.trim() ? value.trim().toLowerCase() : createUsername()
                    ))}
                    variant="outlined"
                    fullWidth
                    required
                    inputProps={{ minLength: 3, maxLength: 64, autoComplete: 'username' }}
                    error={username.length > 0 && !usernameValid}
                    helperText={t('agentMcp.username.helper')}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title={t('agentMcp.actions.generateUsername')}>
                            <IconButton
                              aria-label={t('agentMcp.actions.generateUsername')}
                              onClick={handleGenerateUsername}
                              disabled={isSaving}
                            >
                              <RefreshIcon />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <TextField
                    id="agent-mcp-password"
                    className={styles.textField}
                    label={state.configured
                      ? t('agentMcp.password.newLabel')
                      : t('agentMcp.password.label')}
                    type={passwordVisible ? 'text' : 'password'}
                    value={password}
                    onChange={(event) => {
                      setPassword(event.target.value);
                      setCopiedField(null);
                    }}
                    variant="outlined"
                    fullWidth
                    required
                    inputProps={{
                      autoComplete: 'new-password',
                    }}
                    error={password.length > 0 && !passwordValid}
                    helperText={password.length > 0 && !passwordValid
                      ? t('agentMcp.password.invalid')
                      : t('agentMcp.password.helper')}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {!password ? (
                            <Tooltip title={t('agentMcp.actions.generate')}>
                              <Button
                                className={styles.inlineGenerate}
                                type="button"
                                onClick={handleGenerate}
                                disabled={isSaving}
                                startIcon={<RefreshIcon />}
                                aria-label={t('agentMcp.actions.generate')}
                              >
                                {t('agentMcp.actions.generateShort')}
                              </Button>
                            </Tooltip>
                          ) : (
                            <>
                              <Tooltip title={passwordVisible ? t('agentMcp.password.hide') : t('agentMcp.password.show')}>
                                <IconButton
                                  aria-label={passwordVisible ? t('agentMcp.password.hide') : t('agentMcp.password.show')}
                                  aria-pressed={passwordVisible}
                                  onClick={() => setPasswordVisible(value => !value)}
                                >
                                  {passwordVisible ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={t('agentMcp.copy.password')}>
                                <IconButton
                                  aria-label={t('agentMcp.copy.password')}
                                  onClick={() => handleCopy('password', password, true)}
                                >
                                  <FileCopyOutlinedIcon />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>
                {(copiedField === 'password' || copiedField === 'password-failed') && (
                  <p
                    className={copiedField === 'password-failed' ? styles.copyError : styles.copyFeedback}
                    role="status"
                  >
                    {copiedField === 'password-failed'
                      ? t('agentMcp.copy.failed')
                      : t('agentMcp.copy.copied')}
                  </p>
                )}
              </div>

              <footer className={styles.actionFooter} data-testid="agent-mcp-action-footer">
                <Button
                  className={styles.primaryAction}
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={!canSubmit}
                  startIcon={<VpnKeyIcon />}
                  data-testid="agent-mcp-save"
                >
                  {isSaving
                    ? t('agentMcp.actions.saving')
                    : state.configured
                      ? t('agentMcp.actions.update')
                      : t('agentMcp.actions.saveEnable')}
                </Button>
                {state.configured && (
                  <Button
                    className={styles.revokeAction}
                    type="button"
                    color="secondary"
                    disabled={isSaving}
                    startIcon={<PowerSettingsNewIcon />}
                    onClick={() => setRevokeOpen(true)}
                    data-testid="agent-mcp-revoke"
                  >
                    {t('agentMcp.actions.revoke')}
                  </Button>
                )}
              </footer>
            </form>

            <aside className={styles.guidePanel} aria-labelledby="agent-mcp-client-guide">
              <header>
                <h2 id="agent-mcp-client-guide">{t('agentMcp.clients.title')}</h2>
                <p>{t('agentMcp.clients.description')}</p>
              </header>
              <ul className={styles.clientList}>
                {['claude', 'codex', 'chatgpt'].map(client => (
                  <li key={client}>
                    <span className={styles.clientMark} aria-hidden="true"><VpnKeyIcon /></span>
                    <div>
                      <h3>{t(`agentMcp.clients.${client}.title`)}</h3>
                      <p>{t(`agentMcp.clients.${client}.description`)}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </aside>
          </div>

          <Dialog open={revokeOpen} onClose={() => setRevokeOpen(false)}>
            <DialogTitle>{t('agentMcp.revoke.title')}</DialogTitle>
            <DialogContent>
              <DialogContentText>{t('agentMcp.revoke.description')}</DialogContentText>
            </DialogContent>
            <Divider />
            <DialogActions>
              <Button onClick={() => setRevokeOpen(false)}>{t('common.cancel')}</Button>
              <Button color="secondary" onClick={handleRevoke} disabled={isSaving}>
                {t('agentMcp.actions.revokeConfirm')}
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      )}
    </SearchFeaturePageShell>
  );
}

AgentMcpSettingsPage.propTypes = {
  api: PropTypes.shape({
    getStatus: PropTypes.func.isRequired,
    configure: PropTypes.func.isRequired,
    setEnabled: PropTypes.func.isRequired,
    revoke: PropTypes.func.isRequired,
  }),
};
